import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

// Import Components
import SecondaryButton from '../SecondaryButton';

// Tools & Utils
import { typeFile } from '../../tools/file';
import { dateFormat } from '../../tools/date';

// import logo
import FilePdf from '../../../assets/images/svg/file-pdf.svg';

const Preview = () => {
  //  🌱🌱  Init Navigation  🌱🌱
  const navigate = useNavigate();
  const { id } = useParams();

  //  🌱🌱 Init store(services)  🌱🌱
  const getCollaborator = useStoreActions((actions) => actions.middleware.CollaboratorMiddleware.getCollaborator);
  const getSignedUrlS3 = useStoreActions((actions) => actions.middleware.FileS3.getSignedUrlS3);

  //  🌱🌱  Etats (données)  🌱🌱
  const [collaborator, setCollaborator] = useState({});

  const [typeDocPermis, setTypeDocPermis] = useState(null);
  const [typeDocVm, setTypeDocVm] = useState(null);
  const [typeDocIdentity, setTypeDocIdentity] = useState(null);

  const [signedPermisUrl, setSignedPermisUrl] = useState(null);
  const [signedVmUrl, setSignedVmUrl] = useState(null);
  const [signedIdentityUrl, setSignedIdentityUrl] = useState(null);

  //  🌱🌱 Request call get Collaborator  🌱🌱
  const callGetCollaborator = async () => {
    try {
      const res = await getCollaborator(id);
      setCollaborator(res);

      if (res?.docPermis) {
        setTypeDocPermis(typeFile(res.docPermis));
        const url = await getSignedUrlS3(res.docPermis);
        setSignedPermisUrl(url);
      }

      if (res?.docVm) {
        setTypeDocVm(typeFile(res.docVm));
        const url = await getSignedUrlS3(res.docVm);
        setSignedVmUrl(url);
      }

      if (res?.identityDoc) {
        setTypeDocIdentity(typeFile(res.identityDoc));
        const url = await getSignedUrlS3(res.identityDoc);
        setSignedIdentityUrl(url);
      }
    } catch (error) {
      toast.error('❗️ Une erreur est survenue lors du chargement de la liste des chauffeurs');
    }
  };

  //  🌱🌱  First effect  🌱🌱
  useEffect(() => {
    callGetCollaborator();
  }, []);

  //  🎉🎉  Render  🎉🎉
  return (
    <section className="pt-4 md:pt-14 px-4 md:px-9 h-full flex flex-col">
      <h3 className="font-bold text-xl border-b border-gray-admin pb-[2.125rem]">
        Documents collaborateur {collaborator?.id && `${collaborator.first_name} ${collaborator.last_name}`}
      </h3>

      {typeDocPermis && signedPermisUrl && (
        <div>
          <label className="text-bleu-admin mt-4">Permis de conduire </label>
          <p className="text-sm">Valide jusqu'au : {dateFormat(collaborator.datePermis, '/').full}</p>
          <div className="flex flex-col md:flex-row justify-center">
            {typeDocPermis === 'pdf' ? (
              <div className="border p-4 rounded-lg m-4 flex flex-col items-center justify-center w-full aspect-square">
                <p>PDF</p>
                <img className="h-40 px-10" src={FilePdf} alt="file" />
                <iframe src={`${signedPermisUrl}#toolbar=0`} width="100%" height="100%"></iframe>
                <a
                  className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-gray-admin font-medium text-sm text-center bg-white-admin mt-8"
                  href={signedPermisUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Télécharger
                </a>
              </div>
            ) : (
              <div className="border p-4 rounded-lg m-4 flex flex-col items-center justify-center w-full ">
                <img className="w-full" src={signedPermisUrl} alt="file" />
                <a
                  className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-gray-admin font-medium text-sm text-center bg-white-admin mt-8"
                  href={signedPermisUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Télécharger
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {typeDocVm && signedVmUrl && (
        <div>
          <label className="text-bleu-admin mt-4">Visite Médicale </label>
          <p className="text-sm">Valide jusqu'au : {dateFormat(collaborator.dateVm, '/').full}</p>
          <div className="flex flex-col md:flex-row grow justify-center">
            {typeDocVm === 'pdf' ? (
              <div className="border p-4 rounded-lg m-4 flex flex-col items-center justify-center w-full aspect-square">
                <p>PDF</p>
                <img className="h-40 px-10" src={FilePdf} alt="file" />
                <iframe src={`${signedVmUrl}#toolbar=0`} width="100%" height="100%"></iframe>
                <a
                  className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-gray-admin font-medium text-sm text-center bg-white-admin mt-8"
                  href={signedVmUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Télécharger
                </a>
              </div>
            ) : (
              <div className="border p-4 rounded-lg m-4 flex flex-col items-center justify-center w-full">
                <img className="w-full" src={signedVmUrl} alt="file" />
                <a
                  className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-gray-admin font-medium text-sm text-center bg-white-admin mt-8"
                  href={signedVmUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Télécharger
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================== Carte d'identité ===================== */}
      {typeDocIdentity && signedIdentityUrl && (
        <div>
          <label className="text-bleu-admin mt-4">Carte d'identité </label>
          <p className="text-sm">Valide jusqu'au : {dateFormat(collaborator.dateIdentityDoc, '/').full}</p>
          <div className="flex flex-col md:flex-row grow justify-center">
            {typeDocIdentity === 'pdf' ? (
              <div className="border p-4 rounded-lg m-4 flex flex-col items-center justify-center w-full aspect-square">
                <p>PDF</p>
                <img className="h-40 px-10" src={FilePdf} alt="file" />
                <iframe src={`${signedIdentityUrl}#toolbar=0`} width="100%" height="100%"></iframe>
                <a
                  className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-gray-admin font-medium text-sm text-center bg-white-admin mt-8"
                  href={signedIdentityUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Télécharger
                </a>
              </div>
            ) : (
              <div className="border p-4 rounded-lg m-4 flex flex-col items-center justify-center w-full">
                <img className="w-full" src={signedIdentityUrl} alt="file" />
                <a
                  className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-gray-admin font-medium text-sm text-center bg-white-admin mt-8"
                  href={signedIdentityUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Télécharger
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse gap-y-4 pt-4 mt-9 border-t border-bg-gray-admin pb-5 md:flex-row md:items-center md:justify-between md:mt-1">
        <SecondaryButton title="Retour" onClick={() => navigate(-1)} />
      </div>
    </section>
  );
};

export default Preview;
